import type { ApiEnvelope } from '../../shared/types/index.js';

/** Same-origin `/api` by default (proxied by Vite in dev); override for a separately hosted API. */
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Validation failures (422) carry `[{ message, path }]` in `data`. */
  get isValidationError() {
    return this.status === 422 && Array.isArray(this.data);
  }
}

type QueryValue = string | number | boolean | undefined;

export interface ClientHooks {
  /** Headers added to every request (session token, selected country). */
  headers: () => Record<string, string>;
  /** Called when the server rejects the session (401) on any route except login. */
  onUnauthorized: () => void;
}

let hooks: ClientHooks = { headers: () => ({}), onUnauthorized: () => {} };

/** Lets the session layer supply auth headers without the API modules depending on React. */
export function configureClient(next: ClientHooks): void {
  hooks = next;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

function toQueryString(query: Record<string, QueryValue> = {}): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Calls the SHIELD API and unwraps its `{ success, data | error }` envelope; failures throw `ApiError`. */
export async function request<T>(path: string, { method = 'GET', body, query, signal }: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}${toQueryString(query)}`, {
    method,
    headers: { ...hooks.headers(), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401 && !path.startsWith('/auth/login')) {
    hooks.onUnauthorized();
  }

  const payload = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!payload || typeof payload !== 'object' || !('success' in payload)) {
    throw new ApiError(res.status, `API error: ${res.status}`);
  }
  if (!payload.success) {
    throw new ApiError(payload.error.status_code ?? res.status, payload.error.message, payload.error.data);
  }
  return payload.data;
}
